import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddInstance, ClearInstance } from 'app/area/favourite/action-types';
import {
  selectDefinition,
  selectFetchingDefinition,
  selectFetchingUpdateReplaceDomainNodes,
  selectFetchingReportCategory,
  selectFetchingSortOptions,
  selectPresentationTypeForSelectedReport,
  selectReportCategory,
  selectSearchRequiredUsers,
  selectSortOptions,
  selectSortOrderForSelectedReport,
  selectUrlMultipleSuppliers,
  selectValidateFavouriteFilters,
  selectWorkingDefinition,
  selectFetchingExtendedReplaceDomainNodes,
  selectLayoutTypeForSelectedReport,
  selectDefinitionLoadError
} from 'app/area/favourite/selectors';
import { actionCreators } from './actions';
import { bindActionCreators } from 'redux';
import { DefaultSortOrder } from 'ui/sort-order/types';
import { returnSearchQuery } from 'app/area/favourite/search-query';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { useCanSaveReport } from 'app/area/company-report/hooks';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { STATE } from 'app/constants';
import { searchTranslation, reportTypes } from 'app/area/favourite/constants';
import { AppDispatch } from 'app/infrastructure/configure-store';
import { mapSortOrder } from 'app/area/favourite/mapping';
import { openPrintDialogWhenImagesLoaded } from 'app/infrastructure/platform/print';
import { selectSelectionReportExecuteStatus } from 'app/area/reports/selectors';
import { selectRiskReportExecuteStatus } from 'app/area/case-report/risk-listing/selectors';
import { selectExecuteStatus } from 'app/area/case-report/case-listing/selectors';
import { selectCaseTableViewExecuteStatus } from 'app/area/case-report/case-table-view/selectors';
import { selectBowtieReportExecuteStatus } from 'app/area/bowtie/selectors';
import { LayoutTypeValue } from 'app/area/case-report/constants';

export function useInstanceSlice() {
  const instanceKey = useInstanceKey();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    dispatch(new AddInstance(instanceKey));
    return () => {
      dispatch(new ClearInstance(instanceKey));
    };
  }, [instanceKey]);
}

export function useSavedReportState(editReport: boolean, validateBeforeSave: boolean = false, chartsFetching: boolean = false) {
  const [createReport, setCreateReport] = useState(editReport);
  const [updateExistingReport, setUpdateExistingReport] = useState(editReport);
  const { validateFavouriteFilters } = useActionCreators();
  const canSaveReportResult = useCanSaveReport();
  const canSaveReport = validateBeforeSave ? canSaveReportResult : true;
  const translation = useTranslation(STATE.favourite, searchTranslation);

  // todo: add state with `chartsFetching`
  const [chartsFetchingState, setChartsFetching] = useState(false);
  chartsFetching === true && chartsFetchingState === false && setChartsFetching(true);
  // !!chartsFetching !== chartsFetchingState && setChartsFetching(chartsFetching);

  const onCreateReport = useCallback(() => {
    if (canSaveReport) {
      setUpdateExistingReport(false);
      setCreateReport(true);
    }
    else {
      validateFavouriteFilters(translation);
    }
  }, [canSaveReport, setUpdateExistingReport, setCreateReport, validateFavouriteFilters]);

  const onCloseReport = useCallback(() => {
    setUpdateExistingReport(false);
    setCreateReport(false);
  }, [setUpdateExistingReport, setCreateReport]);

  const onUpdateReport = useCallback(() => {
    if (canSaveReport) {
      setUpdateExistingReport(true);
      setCreateReport(true);
    }
    else {
      validateFavouriteFilters(translation);
    }
  }, [canSaveReport, setUpdateExistingReport, setCreateReport, validateFavouriteFilters]);

  return {
    createReport,
    updateExistingReport,
    chartsFetchingState,
    onCreateReport,
    onCloseReport,
    onUpdateReport
  };
}

export function useFavouriteDefinitions() {
  const instanceKey = useInstanceKey();
  const fetchingDefinition = useSelector(selectFetchingDefinition(instanceKey));
  const definition = useSelector(selectDefinition(instanceKey));
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));
  const loadDefinitionError = useSelector(selectDefinitionLoadError(instanceKey));
  const {
    transformWorkingDefinition: updateDefinition,
    updateReplaceDomainNodes,
    loadExtendedReplaceDomainNodes
  } = useActionCreators();

  return {
    definition,
    workingDefinition,
    updateDefinition,
    updateReplaceDomainNodes,
    loadExtendedReplaceDomainNodes,
    fetchingDefinition,
    loadDefinitionError
  };
}

export function useReplaceDomainNodes() {
  return {
    fetchingUpdateReplaceDomainNodes: useSelector(selectFetchingUpdateReplaceDomainNodes(useInstanceKey())),
    fetchingExtendedReplaceDomainNodes: useSelector(selectFetchingExtendedReplaceDomainNodes(useInstanceKey()))
  };
}

export function useLoadFavouriteDefinitionEffect(favouriteId: number, embeddedFavourite?: boolean, requestPrint?: boolean) {
  const instanceKey = useInstanceKey();
  const printInitiatedRef = useRef(false);

  const {
    clearResults,
    loadFavouriteDefinition,
    loadFavouriteDefinitionEmbedded
  } = useActionCreators();

  useEffect(() => {
    clearResults();
    if (embeddedFavourite) {
      favouriteId && loadFavouriteDefinitionEmbedded({
        favouriteId
      });
    }
    else {
      loadFavouriteDefinition({
        favouriteId,
        location: window.location
      });
    }
  }, [
    instanceKey,
    favouriteId,
    embeddedFavourite
  ]);

  useEffect(() => {
    if (requestPrint && !printInitiatedRef.current) {
      printInitiatedRef.current = true;
      openPrintDialogWhenImagesLoaded();
    }
  }, [requestPrint]);
}

export function useSearchQuery() {
  const instanceKey = useInstanceKey();

  const {
    setWorkingDefinitionQuery
  } = useActionCreators();

  const definition = useSelector(selectDefinition(instanceKey));

  const [searchQueryTemp, setSearchQueryTemp] = useState('');

  const clearSearch = useCallback(() => {
    setSearchQueryTemp('');
  }, [setSearchQueryTemp]);

  const onChange = useCallback((newSearchQueryTemp: string) => {
    if (!newSearchQueryTemp || newSearchQueryTemp === '') {
      clearSearch();
      setWorkingDefinitionQuery('');
    }
    else {
      setSearchQueryTemp(newSearchQueryTemp);
    }
  }, [setWorkingDefinitionQuery]);

  const onSearch = useCallback(() => {
    setWorkingDefinitionQuery(searchQueryTemp);
  }, [setWorkingDefinitionQuery, searchQueryTemp]);

  useEffect(() => {
    const searchQueryFromDefinition = returnSearchQuery(definition?.conditions ?? []) || '';
    setSearchQueryTemp(searchQueryFromDefinition);
  }, [definition]);

  return {
    searchQueryTemp,
    onChange,
    onSearch
  };
}

export function useReport(
  overrideReport?: number,
  loadReportCategories?: boolean
) {
  const instanceKey = useInstanceKey();

  const {
    loadReportCategory,
    setWorkingDefinitionSelectedReport
  } = useActionCreators();

  const fetchingReportCategory = useSelector(selectFetchingReportCategory(instanceKey));
  const reportCategory = useSelector(selectReportCategory(instanceKey)) || [];
  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const report = workingDefinition?.selectedReport?.v;

  useEffect(() => {
    if (loadReportCategories) {
      loadReportCategory();
    }
  }, [instanceKey, loadReportCategories]);

  useEffect(() => {
    if (workingDefinition && overrideReport !== report && overrideReport !== undefined) {
      setWorkingDefinitionSelectedReport(overrideReport);
    }
  }, [setWorkingDefinitionSelectedReport, report, workingDefinition, workingDefinition?.selectedReport?.v, overrideReport]);

  return {
    fetchingReportCategory,
    reportCategory,
    report
  };
}

export function useSortOptions(
  report: number
) {
  const instanceKey = useInstanceKey();
  const fetchingSortOptions = useSelector(selectFetchingSortOptions(instanceKey));
  const sortOptions = useSelector(selectSortOptions(instanceKey));
  const sortOrderFields = mapSortOrder(useSelector(selectSortOrderForSelectedReport(instanceKey)) || []);

  const {
    loadSortOptions,
    setWorkingDefinitionSortOptions
  } = useActionCreators();

  const loadSortOptionsForCurrentReport = useCallback(
    (query?: string) => loadSortOptions(report, query),
    [report]
  );

  const updateSortOptions = useCallback((fields: DefaultSortOrder[]) => {
    setWorkingDefinitionSortOptions(report, fields);
  }, [report]);

  return {
    fetchingSortOptions,
    loadSortOptions: loadSortOptionsForCurrentReport,
    updateSortOptions,
    sortOptions,
    sortOrderFields
  };
}

export function useActionCreators() {
  const instanceKey = useInstanceKey();
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators(instanceKey), dispatch), [instanceKey]);
}

export function useSearchRequiredUsers() {
  const instanceKey = useInstanceKey();
  return useSelector(selectSearchRequiredUsers(instanceKey));
}

export function useUrlMultipleSuppliers() {
  const instanceKey = useInstanceKey();
  return useSelector(selectUrlMultipleSuppliers(instanceKey));
}

export function usePresentationType() {
  const instanceKey = useInstanceKey();
  return useSelector(selectPresentationTypeForSelectedReport(instanceKey));
}

export function useValidateFavouriteFilters() {
  const instanceKey = useInstanceKey();
  return useSelector(selectValidateFavouriteFilters(instanceKey));
}

export function useExecuteStatus() {
  const instanceKey = useInstanceKey();
  const report = useSelector(selectWorkingDefinition(instanceKey))?.selectedReport?.v;

  // Call all selectors unconditionally
  const selectionReportStatus = useSelector(selectSelectionReportExecuteStatus(instanceKey));
  const riskReportStatus = useSelector(selectRiskReportExecuteStatus(instanceKey));
  const caseTableViewStatus = useSelector(selectCaseTableViewExecuteStatus(instanceKey));
  const bowtieReportStatus = useSelector(selectBowtieReportExecuteStatus(instanceKey));
  const caseListingStatus = useSelector(selectExecuteStatus(instanceKey));
  const selectedLayoutType = useSelector(selectLayoutTypeForSelectedReport(instanceKey));

  // Then determine which one to return based on report type and layout type
  if (report === reportTypes.selectionReport) {
    return selectionReportStatus;
  } else if (report === reportTypes.frequencyReport) {
    return selectionReportStatus;
  } else if (report === reportTypes.riskReport) {
    return riskReportStatus;
  } else if (report === reportTypes.bowtieReport && selectedLayoutType?.v === LayoutTypeValue.bowtie) {
    return bowtieReportStatus;
  } else if (selectedLayoutType?.v === LayoutTypeValue.table) {
    return caseTableViewStatus;
  } else if (selectedLayoutType?.v === LayoutTypeValue.bowtie) {
    return bowtieReportStatus;
  } else if (selectedLayoutType?.v === LayoutTypeValue.tiles) {
    return caseListingStatus;
  }

  return caseListingStatus;
}
